import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { appConfig } from '../../owner.config';
import { catchError, map } from 'rxjs/operators';
import {BannerResponse} from "../models/banner.interface";

@Injectable()
export class ContentfulService {
  constructor(private http: HttpClient) {
    if (this.token) {
      this.headers = new HttpHeaders().set(
        'Authorization',
        `Bearer ${this.token}`
      );
    } else {
      this.headers = new HttpHeaders();
    }
  }

  public token = localStorage.getItem('token');
  public headers: HttpHeaders;

  getContentAnimals<T = any>(categories: string[], petszelAnimalId: string, petszelOwnerId: string): Observable<T> {
    const url = `${appConfig.apiUrl}Owner/Content/Animals/${petszelAnimalId}/Summary?tagIds=${categories}&matchAllTags=true`;
    return this.http.get<T>(url, { headers: this.headers, params: { petszelOwnerId } });
  }

  getContent<T = any>(
    id: string, // This can be a slugId or an entryId
    idType: 'slug' | 'entry', // Specify the type of the id
    petszelAnimalId: string,
    petszelOwnerId: string
  ): Observable<{ id: string; read: boolean; data: T }> {

    let url = `${appConfig.apiUrl}Owner/Content/Animals/${petszelAnimalId}`;

    // Append the appropriate query parameter based on the id type
    url += idType === 'slug' ? `?slugId=${id}` : `?entryId=${id}`;

    return this.http.get<T>(url, { headers: this.headers, params: { petszelOwnerId } }).pipe(
      catchError((error: any) => throwError(error)),
      map((data: T) => ({
        id: id,
        read: (data as any)?.read === true,
        data: data,
      }))
    );
  }

  getEntryById(id: string, petszelAnimalId: string, idType: 'slug' | 'entry', petszelOwnerId: string): Observable<any> {
    if (!id || !petszelAnimalId) {
      // Handle error if id or petszelAnimalId is null or undefined
      return throwError('ID not provided');
    }

    // Determine the query parameter based on the type of the ID
    const queryParam = idType === 'slug' ? `slugId=${id}` : `entryId=${id}`;
    const url = `${appConfig.apiUrl}Owner/Content/Animals/${petszelAnimalId}?${queryParam}`;

    return this.http.get(url, { headers: this.headers, params: { petszelOwnerId } });
  }

  getCtaProduct(): Observable<any>{
    const url = `${appConfig.apiUrl}Owner/Content/ProductsAndServices`;
    return this.http.get(url);
  }

  getAppBasic(slugId: string, petszelAnimalId: string, shelterAccountId: string, petszelOwnerId: string): Observable<any> {
    const url = `${appConfig.apiUrl}Owner/Content/AppBasicPage/slug`;
    return this.http.get<any>(url, {
      headers: this.headers,
      params: { slugId, petszelAnimalId, shelterAccountId, petszelOwnerId }
    });
  }

  getContentById(entryId: string, petszelOwnerId: string, shelterAccountId: string): Observable<any> {
    const url = `${appConfig.apiUrl}Owner/Content`;
    return this.http.get<any>(url, {
      headers: this.headers,
      params: { entryId, petszelOwnerId, shelterAccountId }
    }).pipe(
      catchError((error: any) => throwError(error))
    );
  }

  getAppBasicPageById(entryId: string, petszelOwnerId: string, shelterAccountId: string, petszelAnimalId: string): Observable<any> {
    const url = `${appConfig.apiUrl}Owner/Content/AppBasicPage`;
    return this.http.get<any>(url, {
      headers: this.headers,
      params: { entryId, petszelOwnerId, shelterAccountId, petszelAnimalId }
    }).pipe(
      catchError((error: any) => throwError(error))
    );
  }

  getHomeBannerContent(): Observable<BannerResponse> {
    const url = `${appConfig.apiUrl}StaticContent/Content/HomePage/BannerAdPriority`;
    return this.http.get<BannerResponse>(url, { headers: this.headers }).pipe(
      map((response: BannerResponse) => {
        // Validate banner placement
        if (response.bannerPlacement !== 'Banner: App Home Page') {
          throw new Error(`Invalid banner placement: ${response.bannerPlacement}. Expected: "Banner: App Home Page"`);
        }
        return response;
      }),
      catchError((error) => {
        console.error('Banner service error:', error);
        return throwError(() => error);
      })
    );
  }

  getBannerForAnimal(petszelAnimalId: string): Observable<any> {
    const url = `${appConfig.apiUrl}Owner/Content/Animals/${petszelAnimalId}/Banners/Taxonomy`;
    return this.http.get<any>(url, {
      headers: this.headers
    }).pipe(
      catchError((error) => {
        console.error('Taxonomy banner service error:', error);
        return throwError(() => error);
      })
    );
  }

  getHillsArticlesForAnimal(petszelAnimalId: string, petszelOwnerId: string): Observable<any> {
    const url = `${appConfig.apiUrl}Owner/Content/Hills/Articles/${petszelAnimalId}`;
    return this.http.get<any>(url, {
      headers: this.headers,
      params: {petszelOwnerId}
    }).pipe(
      catchError((error) => {
        console.error('Hills articles service error:', error);
        return throwError(() => error);
      })
    );
  }
}
